import { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import {
  registerMember,
  getGatewayPaymentStatus,
  getMembershipFeeConfigsByLibrary,
  getErrorMessage
} from '../../api/api';

export const initialRegisterForm = {
  member_source: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  gender: '',
  dob: '',
  address: '',
  city: '',
  state: '',
  country: 'India',
  pincode: '',
  admission_no: '',
  relation_type: ''
};

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PINCODE_REGEX = /^\d{6}$/;

const isStaff = (roleNameOrId) => {
  const role = String(roleNameOrId?.role_name || '').toLowerCase();
  const id = Number(roleNameOrId?.role_id);
  return role === 'admin' || role === 'librarian' || id === 1 || id === 3;
};

const isValidPastDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date <= new Date();
};

// Every registration entry point (the Members-page modal and the
// backward-compatible /librarian/members/register page) shares this hook so
// validation, the register call and the post-registration payment flow are
// defined exactly once.
export default function useRegisterMemberWorkflow() {
  const { user } = useAuth();
  const libraryId = useActiveLibraryId();
  const canCollectPayment = isStaff(user);

  const [form, setForm] = useState(initialRegisterForm);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [result, setResult] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [lastOrderId, setLastOrderId] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [outsiderDepositAmount, setOutsiderDepositAmount] = useState(null);

  const isSchoolParent = form.member_source === 'school_parent';

  // The Outsider option shows its required security deposit up front (before
  // submission) so a librarian isn't surprised by it after registering —
  // always the server-configured amount, never a client-guessed figure.
  useEffect(() => {
    let cancelled = false;

    const loadOutsiderDeposit = async () => {
      if (!libraryId) return;

      try {
        const configs = await getMembershipFeeConfigsByLibrary(libraryId);
        if (cancelled) return;

        const activeOutsiderConfig = (configs || []).find(
          (config) => config.member_source === 'outsider' && config.status === 'active'
        );

        setOutsiderDepositAmount(
          activeOutsiderConfig ? Number(activeOutsiderConfig.security_deposit_amount) : null
        );
      } catch {
        // Non-fatal — the toggle simply won't show a deposit hint; actual
        // eligibility/amount is still fully enforced server-side at submit.
        if (!cancelled) setOutsiderDepositAmount(null);
      }
    };

    loadOutsiderDeposit();

    return () => {
      cancelled = true;
    };
  }, [libraryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileImageChange = (e) => {
    setProfileImageFile(e.target.files?.[0] || null);
  };

  // Switching Member Type clears the fields that only apply to the other
  // type — an outsider's payload must never carry admission_no/relation_type
  // (and must not silently inherit a leftover "Guardian" default), and a
  // school parent should start from a clean relation choice each time.
  const handleMemberSourceChange = (member_source) => {
    setForm((prev) => ({
      ...prev,
      member_source,
      admission_no: member_source === 'school_parent' ? prev.admission_no : '',
      relation_type: member_source === 'school_parent' ? (prev.relation_type || 'guardian') : ''
    }));
  };

  const hasUnsavedChanges = () => {
    if (result) return false;
    return Object.keys(initialRegisterForm).some((key) => form[key] !== initialRegisterForm[key]);
  };

  const resetAll = () => {
    setForm(initialRegisterForm);
    setProfileImageFile(null);
    setResult(null);
    setPaymentStatus('pending');
    setLastOrderId(null);
    setShowPaymentModal(false);
  };

  const validate = () => {
    if (!libraryId) {
      Swal.fire('Error', 'Library not found', 'error');
      return false;
    }
    if (!form.member_source) {
      Swal.fire('Error', 'Please select a member type.', 'error');
      return false;
    }
    if (!form.first_name.trim()) {
      Swal.fire('Error', 'First name is required', 'error');
      return false;
    }
    if (!PHONE_REGEX.test(form.phone.trim())) {
      Swal.fire('Error', 'Enter a valid 10-digit mobile number', 'error');
      return false;
    }
    if (!form.password.trim()) {
      Swal.fire('Error', 'Password is required', 'error');
      return false;
    }
    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) {
      Swal.fire('Error', 'Enter a valid email address', 'error');
      return false;
    }
    if (form.dob && !isValidPastDate(form.dob)) {
      Swal.fire('Error', 'Enter a valid date of birth (must not be in the future)', 'error');
      return false;
    }
    if (form.pincode.trim() && !PINCODE_REGEX.test(form.pincode.trim())) {
      Swal.fire('Error', 'Enter a valid 6-digit pincode', 'error');
      return false;
    }
    if (isSchoolParent && !form.admission_no.trim()) {
      Swal.fire('Error', 'Admission number is required for a school parent', 'error');
      return false;
    }
    if (isSchoolParent && !form.relation_type) {
      Swal.fire('Error', 'Relation to student is required for a school parent', 'error');
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e?.preventDefault?.();
    if (!validate() || saving) return;

    const payload = {
      library_id: libraryId,
      member_source: form.member_source,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim(),
      password: form.password,
      gender: form.gender || null,
      dob: form.dob || null,
      address: form.address || null,
      city: form.city || null,
      state: form.state || null,
      country: form.country || null,
      pincode: form.pincode.trim() || null
    };

    // Outsiders never carry admission_no/relation_type — not even as null
    // fields — keeping the request shape honest about what each member
    // type actually needs.
    if (isSchoolParent) {
      payload.admission_no = form.admission_no.trim();
      payload.relation_type = form.relation_type;
    }

    // Only switch to multipart when a photo was actually picked — keeps the
    // common (no-photo) case exactly as before.
    let finalPayload = payload;

    if (profileImageFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        fd.append(key, value ?? '');
      });
      fd.append('profile_image', profileImageFile);
      finalPayload = fd;
    }

    try {
      setSaving(true);
      const response = await registerMember(finalPayload);
      setResult(response.data);
      setPaymentStatus(response.data.payment_status === 'not_required' ? 'success' : 'pending');
      return response;
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!lastOrderId || checkingStatus) return;

    try {
      setCheckingStatus(true);
      const status = await getGatewayPaymentStatus(lastOrderId);

      if (status.status === 'success') {
        setPaymentStatus('success');
        Swal.fire('Success', 'Payment confirmed. Membership is now active.', 'success');
      } else if (status.status === 'failed') {
        Swal.fire('Payment failed', 'The payment did not complete. You can retry.', 'error');
      } else {
        Swal.fire('Still pending', 'Payment has not been confirmed yet. Please check again shortly.', 'info');
      }
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setCheckingStatus(false);
    }
  };

  return {
    libraryId,
    canCollectPayment,
    form,
    setForm,
    profileImageFile,
    handleProfileImageChange,
    saving,
    result,
    setResult,
    paymentStatus,
    setPaymentStatus,
    lastOrderId,
    setLastOrderId,
    checkingStatus,
    showPaymentModal,
    setShowPaymentModal,
    outsiderDepositAmount,
    isSchoolParent,
    handleChange,
    handleMemberSourceChange,
    hasUnsavedChanges,
    resetAll,
    validate,
    handleRegister,
    handleCheckStatus
  };
}
